<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'

const props = defineProps<{
	align?: 'left' | 'right'
}>()

const isOpen = ref(false)
const triggerRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)

const position = ref({ top: 0, left: 0 })

const toggle = async () => {
	isOpen.value = !isOpen.value
	if (isOpen.value) {
		await nextTick()
		updatePosition()
	}
}

const close = () => {
	isOpen.value = false
}

const updatePosition = () => {
	if (!triggerRef.value || !dropdownRef.value) return

	const rect = triggerRef.value.getBoundingClientRect()
	const dropdownHeight = dropdownRef.value.offsetHeight

	const spaceBelow = window.innerHeight - rect.bottom
	const spaceAbove = rect.top

	let top = rect.bottom

	if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
		top = rect.top - dropdownHeight
	}

	let left =
		props.align === 'right'
			? rect.right - dropdownRef.value.offsetWidth
			: rect.left

	left = Math.max(8, Math.min(left, window.innerWidth - dropdownRef.value.offsetWidth - 8))

	position.value = {
		top,
		left
	}
}

const handleClickOutside = (event: MouseEvent) => {
	if (
		dropdownRef.value &&
		!dropdownRef.value.contains(event.target as Node) &&
		!triggerRef.value?.contains(event.target as Node)
	) {
		close()
	}
}

onMounted(() => {
	document.addEventListener('mousedown', handleClickOutside)
	window.addEventListener('resize', updatePosition)
	window.addEventListener('scroll', updatePosition, true)
})

onUnmounted(() => {
	document.removeEventListener('mousedown', handleClickOutside)
	window.removeEventListener('resize', updatePosition)
	window.removeEventListener('scroll', updatePosition, true)
})
</script>

<template>
	<div class="inline-block" ref="triggerRef">
		<div @click="toggle">
			<slot name="trigger" />
		</div>
	</div>

	<teleport to="body">
		<transition name="zoom">
			<div
				v-if="isOpen"
				ref="dropdownRef"
				class="fixed z-[9999] w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5"
				:style="{
          top: position.top + 'px',
          left: position.left + 'px'
        }"
			>
				<div class="py-1" @click.stop="close">
					<slot />
				</div>
			</div>
		</transition>
	</teleport>
</template>
